import { FC } from 'react'
import styled, { css, keyframes, useTheme } from 'styled-components'
import {
  Capitalized,
  Capitalized2,
  Small,
  Small3,
  Tiny,
  Tiny2
} from '@/components/atoms/typography'
import {
  display,
  minWidth,
  width,
  space,
  margin,
  padding,
  flexGrow,
  color
} from 'styled-system'
import { transparentize } from 'polished'
import { validFor } from '@/theme/validFor'
import { MenuCloseIconDefault } from './icons/MenuCloseIconDefault'
import { NextArrowIconDefault } from './icons/NextArrowIconDefault'
import { Rating } from 'react-simple-star-rating'
import { CheckIconDefault } from './icons/CheckIconDefault'
import { ResetIconDefault } from '@/components/atoms/icons/ResetIconDefault'
import { Box } from '@/components/atoms/Grid'

type ButtonProps = {
  isDisabled?: boolean
  isLoading?: boolean
  title: string
  onClick: (e: Event) => void
} & Rest

const Button: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  isLoading,
  children,
  ...rest
}) => {
  const isButtonDisabled = isDisabled || isLoading
  return (
    <StyledButton
      onClick={makeOnClickAction(isDisabled, onClick)}
      isDisabled={isButtonDisabled}
      disabled={isButtonDisabled}
      {...rest}
    >
      <ContentWrapper my={14} mx={38}>
        {title || children}
        {isLoading && <Spinner />}
      </ContentWrapper>
    </StyledButton>
  )
}

export const BorderedButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => (
  <StyledBorderedButton
    onClick={makeOnClickAction(isDisabled, onClick)}
    isDisabled={isDisabled}
    {...rest}
  >
    <Small my={1} mx={3}>
      {title}
    </Small>
  </StyledBorderedButton>
)

export const SmallBorderedButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => (
  <StyledBorderedButton
    onClick={makeOnClickAction(isDisabled, onClick)}
    isDisabled={isDisabled}
    {...rest}
  >
    <Tiny2 my={1} mx={3}>
      {title}
    </Tiny2>
  </StyledBorderedButton>
)

export const ConfirmButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => (
  <StyledConfirmButton
    onClick={makeOnClickAction(isDisabled, onClick)}
    isDisabled={isDisabled}
    {...rest}
  >
    <Capitalized my={15} mx={3}>
      {title}
    </Capitalized>
  </StyledConfirmButton>
)

export const SmallBlackButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => (
  <StyledSmallBlackButton
    onClick={makeOnClickAction(isDisabled, onClick)}
    isDisabled={isDisabled}
    {...rest}
  >
    <Tiny2 m={0}>{title}</Tiny2>
  </StyledSmallBlackButton>
)

export const TightBlackButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => (
  <StyledSmallBlackButton
    onClick={makeOnClickAction(isDisabled, onClick)}
    isDisabled={isDisabled}
    {...rest}
  >
    <Tiny my="8px">{title}</Tiny>
  </StyledSmallBlackButton>
)

export const BigBlackButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => {
  const theme = useTheme()
  return (
    <StyledBigButton
      backgroundColor={theme.colors.onBackground}
      onClick={makeOnClickAction(isDisabled, onClick)}
      isDisabled={isDisabled}
      {...rest}
    >
      <Capitalized2 m={0}>{title}</Capitalized2>
    </StyledBigButton>
  )
}

export const BigAccentButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => {
  const theme = useTheme()
  return (
    <StyledBigButton
      backgroundColor={theme.colors.accent}
      onClick={onClick}
      isDisabled={isDisabled}
      {...rest}
    >
      <Capitalized2 m={0}>{title}</Capitalized2>
    </StyledBigButton>
  )
}

export const BigGrayButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  ...rest
}) => {
  const theme = useTheme()
  return (
    <StyledBigButton
      backgroundColor={transparentize(0.97, theme.colors.onBackground)}
      color={theme.colors.onBackground}
      onClick={onClick}
      isDisabled={isDisabled}
      {...rest}
    >
      <Capitalized2 m={0}>{title}</Capitalized2>
    </StyledBigButton>
  )
}

export const RateButton: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled,
  arrowWidth,
  arrowHeight,
  starSize,
  rating,
  nextArrow,
  ...rest
}) => {
  const theme = useTheme()

  return (
    <StyledRateButton
      onClick={onClick}
      isDisabled={isDisabled}
      rating={rating}
      nextArrow={nextArrow}
      {...rest}
    >
      {rating && (
        <Rating
          SVGstrokeColor={theme.colors.background}
          SVGstorkeWidth={1}
          emptyColor={theme.colors.toolbar}
          allowFraction={true}
          size={starSize}
          readonly={true}
          {...rest}
        />
      )}
      <Small3 m={0}>{title}</Small3>
      {nextArrow && (
        <NextArrowIconDefault
          color={theme.colors.background}
          width={arrowWidth}
          height={arrowHeight}
        />
      )}
    </StyledRateButton>
  )
}

export const ConfirmButtonRedesign: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled = false
}) => {
  const theme = useTheme()
  return (
    <StyledConfirmButtonRedesign onClick={onClick} isDisabled={isDisabled}>
      <CheckIconDefault
        width={14}
        height={14}
        color={theme.colors.background}
      />
      <StyledCapitalized m={0}>{title}</StyledCapitalized>
    </StyledConfirmButtonRedesign>
  )
}

export const ResetButtonRedesign: FC<ButtonProps> = ({
  title,
  onClick,
  isDisabled = false
}) => {
  const theme = useTheme()
  return (
    <StyledResetButtonRedesign onClick={onClick} isDisabled={isDisabled}>
      <ResetIconDefault
        width={14}
        height={14}
        color={theme.colors.onBackground}
      />
      <StyledCapitalized m={0}>{title}</StyledCapitalized>
    </StyledResetButtonRedesign>
  )
}

export const CloseIconButton: FC<
  { onClick: (e) => void; showBackground?: boolean } & Rest
> = ({ onClick, showBackground = true, ...rest }) =>
  showBackground === true ? (
    <StyledIconButton onClick={onClick} aria-label="Close promotional banner">
      <MenuCloseIconDefault opacity={0.5} width={13} height={13} {...rest} />
    </StyledIconButton>
  ) : (
    <StyledIconButton
      onClick={onClick}
      backgroundColor="transparent"
      aria-label="Close promotional banner"
    >
      <MenuCloseIconDefault opacity={0.5} width={13} height={13} {...rest} />
    </StyledIconButton>
  )

const makeOnClickAction =
  (isDisabled: boolean, onClick: (e: Event) => void): ((e: Event) => void) =>
  (e) => {
    e.preventDefault()
    !isDisabled && onClick(e)
  }

const ButtonStyleProps = css`
  ${space};
  ${width};
  ${display};
  ${minWidth};
  ${margin};
  ${padding};
  ${flexGrow};
  ${color};
`

const ContentWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 12px;
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled(Box)`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.theme.colors.background};
  border-top-color: ${(props) => props.theme.colors.onBackground};
  animation: ${spin} 0.6s linear infinite;
`

const makeBackgroundHoverColor = (
  color: string,
  isDisabled: boolean,
  amount = 0.2
): css => css`
  ${validFor.desktop`
  &:hover {
    ${!isDisabled ? `background-color: ${transparentize(amount, color)}` : ''}; 
  }
`}
`

const ButtonBase = css`
  background: none;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};
  &:hover {
    ${(props) => !props.isDisabled && 'cursor: pointer;'};
  }
  ${ButtonStyleProps};
`

const ButtonBaseWithDefaultHoverColor = css`
  ${ButtonBase};
  ${(props) =>
    makeBackgroundHoverColor(
      props.theme.colors.onBackground,
      props.isDisabled,
      0.97
    )};
`

const StyledIconButton = styled.button`
  padding: 5px;
  border: 0;
  border-radius: 50% !important;
  ${ButtonBaseWithDefaultHoverColor}
`

const StyledBorderedButton = styled.button`
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  border: solid 1px #979797;
  ${ButtonBaseWithDefaultHoverColor}
`

const StyledButton = styled.button`
  ${ButtonBaseWithDefaultHoverColor};
  padding: 0;
  border: 0;
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  ${ButtonStyleProps};
`

export const BlackButton = styled(Button)`
  background-color: ${(props) => props.theme.colors.onBackground};
  color: ${(props) => props.theme.colors.background};
  ${(props) =>
    makeBackgroundHoverColor(
      props.theme.colors.onBackground,
      props.isDisabled
    )};
`

export const AccentButton = styled(Button)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  ${(props) =>
    makeBackgroundHoverColor(props.theme.colors.accent, props.isDisabled)};
`

const StyledConfirmButton = styled.button`
  ${ButtonBase};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  border: 0;
  background-color: ${(props) => props.theme.colors.accentSecondary};
  color: ${(props) => props.theme.colors.background};
  ${(props) =>
    makeBackgroundHoverColor(
      props.theme.colors.accentSecondary,
      props.isDisabled
    )};
  ${ButtonStyleProps};
`

const StyledSmallBlackButton = styled.button`
  ${ButtonBase};
  border: 0;
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  background-color: ${(props) => props.theme.colors.onBackground};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  padding: 0 9px;
  ${(props) =>
    makeBackgroundHoverColor(
      props.theme.colors.onBackground,
      props.isDisabled
    )};
  ${ButtonStyleProps};
`

const StyledBigButton = styled.button`
  ${ButtonBase};
  text-transform: uppercase;
  border: 0;
  color: ${(props) => props.color || props.theme.colors.background};
  padding: 15px 12px;
  ${(props) =>
    makeBackgroundHoverColor(
      props.backgroundColor || props.theme.colors.background,
      props.isDisabled
    )};
  ${ButtonStyleProps};
`

const StyledRateButton = styled.button`
  display: grid;
  grid-template-columns: 4fr 4fr 1fr;
  grid-template-rows: 1fr;
  align-items: center;
  width: 180px;
  height: 36px;
  color: ${(props) => props.theme.colors.background};
  background-color: ${(props) => props.theme.colors.accent};
  border: none;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`

export const ButtonBaseNew = css`
  border: 0.5px solid ${(props) => props.theme.colors.buttonBorderNew};
  border-radius: ${(props) => props.theme.buttonRadiusRadiusNew}px;
  font-weight: 400;
  font-size: 16px;
  color: ${(props) =>
    props.disabled ? '#999' : props.theme.colors.buttonColorNew};
  background-color: ${(props) => props.theme.colors.background};
  padding: 8px 12px;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`
export const ButtonBaseHoverNew = css`
  ${ButtonBaseNew}

  ${validFor.desktop`
    &:hover:not(:disabled) {
      background-color: #efefef;
      cursor: pointer;
    }
  `}
`

const StyledConfirmButtonRedesign = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.onBackground};
  color: ${(props) => props.theme.colors.background};
  transition: background-color 0.2s ease;
  height: 33px;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${(props) =>
      props.theme.colors.onBackgroundHover || '#2a2a2a'};
  }
`

const StyledCapitalized = styled(Capitalized)`
  font-size: 14px;
  font-weight: 500;
`

const StyledResetButtonRedesign = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px;
  border: 1px solid ${(props) => props.theme.colors.buttonBorderNew};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.onBackground};
  transition: background-color 0.2s ease;
  height: 33px;
  ${ButtonBaseWithDefaultHoverColor}
  &:hover {
    cursor: pointer;
  }
`
